import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { push } from "connected-react-router";
import { useTranslation } from "react-i18next";
import { Actions as AuthActions } from "../../../store/ducks/auth";
import Dygraph from "dygraphs";
import {
  Container,
  Avatar,
  Snackbar,
  Box,
  Button,
  Grid,
  Tooltip,
  Typography,
  Link,
  Badge,
  Card,
  CardContent,
  CardActions,
  Slider,
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import AvatarGroup from "@material-ui/lab/AvatarGroup";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import googleLogo from "../../../resources/googleLogo.svg";
import geeLogo from "../../../resources/gee_icon.png";
import googleLogoDisabled from "../../../resources/googleLogoDisabled.svg";
import Slide from "@material-ui/core/Slide";
import HomePageLayout from "../../components/homepage/HomePageLayout";
import { useLocalStorage } from "../../../common/utils";
import { Bar, Chart } from "react-chartjs-2";
// useStyles is a hook for Material-UI's styling.
const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: "#032736",
    minHeight: "100vh",
    padding: theme.spacing(4),
    color: "white",
  },
  paper: {
    backgroundColor: "#0ea28c",
    padding: theme.spacing(4),
    borderRadius: "8px",
    boxShadow: `0px 4px 10px rgba(0, 0, 0, 0.2)`,
    color: "white",
  },
  button: {
    backgroundColor: "#5ad9bf",
    color: "#032736",
    border: `2px solid white`,
    "&:hover": {
      backgroundColor: "#0ea28c",
      color: "white",
    },
  },
  header: {
    color: "#5ad9bf",
    fontWeight: "bold",
    marginBottom: theme.spacing(2),
  },
  outlinedBox: {
    border: `2px solid #5ad9bf`,
    borderRadius: "8px",
    padding: theme.spacing(2),
    color: "white",
  },
  backdrop: {
    height: "100vh",
    width: "100vw",
    position: "fixed",
    zIndex: "-1",
    top: 0,
    left: 0,
    backgroundColor: theme.palette.primary.light,
  },
  heading: {
    backgroundColor: theme.palette.primary.main,
    background: "url(/gyn2.jpg)",
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundAttachment: "fixed",
    minHeight: "75vh",
  },
  bay: {
    padding: "60px 0px",
    background: "url(/bay.jpg)",
    backgroundSize: "cover",
    backgroundPosition: "center",
  },
  risc: {
    padding: "60px 0px",
    background: "url(/port.jpg)",
    backgroundSize: "cover",
    backgroundPosition: "center",
  },
  headingContainer: {
    paddingBottom: "10vh",
    paddingTop: "15vh",
  },
  content: {
    padding: "60px 0px",
  },
  fullW: {
    with: "100%",
  },
  flex: {
    display: "flex",
  },
  logo: {
    fontSize: 120,
    color: "white",
    margin: "0px 0px 20px 0px",
  },
  title: {
    fontWeight: "600",
    marginBottom: theme.spacing(5),
  },
  subtitle: {
    fontSize: "1.5rem",
  },
  wrapper: {
    display: "flex",
    alignItems: "center",
    flexFlow: "column",
    backgroundColor: theme.palette.primary.secondary,
    padding: "40px 48px",
  },
  i18nSwitchers: {
    textAlign: "right",
    backgroundColor: "white",
  },
  i18nSwitch: {
    padding: "5px 10px",
    margin: "0 10px",
  },
  flag: {
    marginRight: "5px",
    height: "25px",
    width: "25px",
  },
  description: {
    margin: "30px 0px 40px 0px",
    color: "rgba(0, 0, 0, 0.85)",
  },
  google: {
    margin: "-4px 20px -4px -15px",
  },
  intro_gif: {
    maxWidth: "100%",
  },
  spaced_text: {
    marginBottom: theme.spacing(4),
  },
  bay_text: {
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(5),
    borderRadius: theme.spacing(1),
  },
  spaced_btn: {
    marginRight: "16px",
  },
  large: {
    width: theme.spacing(12),
    height: theme.spacing(12),
    border: `2px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
  },
  small: {
    width: theme.spacing(4),
    height: theme.spacing(4),
    border: `2px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
  },
  sponsor_img: {
    height: theme.spacing(6),
    maxWidth: "100%",
    marginBottom: theme.spacing(2),
  },
  sponsor_img_sqr: {
    height: theme.spacing(13),
    maxWidth: "100%",
    marginBottom: theme.spacing(2),
  },
  sponsor_img_partner: {
    height: theme.spacing(10),
    maxWidth: "100%",
    marginBottom: theme.spacing(2),
  },
  avatar_link: {
    border: "none",
  },
  snack: {
    maxWidth: "75%",
  },
  cpnq_logo: {
    maxWidth: "150px",
  },
}));

// Create a custom Typography component.
const WhiteTextTypography = withStyles({
  root: {
    color: "#FFFFFF",
  },
})(Typography);

// Create a custom Avatar component.
const SmallAvatar = withStyles((theme) => ({
  root: {
    width: theme.spacing(5),
    height: theme.spacing(5),
    backgroundColor: theme.palette.background.paper,
    border: `2px solid ${theme.palette.divider}`,
    borderRadius: ".75rem",
  },
}))(Avatar);

// Create a transition component.
function TransitionLeft(props) {
  return <Slide {...props} direction="left" />;
}

// main function
function HomePage() {
  // check if the authentication is busy
  const busy = useSelector((state) => state.auth.authenticating);

  const dispatch = useDispatch();
  // use i18n
  const [t] = useTranslation();
  // use custom styles
  const classes = useStyles();

  // dispatch Authentication action with useEffect
  useEffect(() => {
    dispatch(AuthActions.loadClientAuth());
  }, []);

  // create devAdvOpen state
  const [devAdvOpen, setDevAdvOpen] = React.useState(true);

  // create cookiesAdvOpen state
  const [cookiesAdvOpen, setCookiesAdvOpen] = useLocalStorage(
    "showNewCookiesADV",
    true
  );

  // function to handle devAdvClose event (from devAdvOpen) deppendin on the reason
  const handleDevAdvClose = (reason) => {
    // if the reason isn't "clickaway" we close the devAdv
    if (reason !== "clickaway") {
      setDevAdvOpen(false);
    }
  };

  // function to handle cookiesAccept event (from cookiesAdvOpen) deppendin on the reason
  const handleCookiesAccept = (reason) => {
    // if the reason isn't "clickaway" we close the cookiesAdv and post the cookiesAccept event to the API
    if (reason !== "clickaway") {
      setCookiesAdvOpen(false);
      // postAcceptingToAPI();
    }
  };

  // function that send a POST request to the API to accept cookies
  const postAcceptingToAPI = () => {
    fetch("https://cassie-api.vercel.app/api/cookies", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        read: true,
      }),
    })
      .then((response) => {
        // if we get a bad response we show the error
        if (response.status >= 400) {
          throw new Error("Bad response from server");
        }
      })
      .catch((error) => console.error("Error: ", error));
  };

  return (
    <HomePageLayout>
      <Box className={classes.heading}>
        <Container
          className={classes.headingContainer}
          maxWidth="md"
          display="flex"
          flexdirection="column"
        >
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <WhiteTextTypography className={classes.title} variant="h2">
                {t("self.fullTitle")}
              </WhiteTextTypography>
            </Grid>

            <Grid item xs={12} md={8}>
              <WhiteTextTypography
                className={classes.subtitle}
                variant="body1"
                style={{
                  backgroundColor: "rgba(0, 0, 0, 0.50)", // Black with 75% transparency
                  padding: "4px 8px", // Add padding for better appearance
                  borderRadius: "4px", // Optional: Add rounded corners
                }}
              >
                {t("self.shortDesc")}
              </WhiteTextTypography>
            </Grid>

            <Grid item xs={12}>
              <Button
                // disabled={busy}
                variant="contained"
                style={{ marginTop: "10px" }}
                size="large"
                color="primary"
                className={classes.spaced_btn}
                // start Authentication proccess when the button is clicked
                onClick={() =>
                  dispatch(
                    AuthActions.begin(() => dispatch(push("/main/selection")))
                  )
                }
              >
                <Avatar
                  className={classes.google}
                  variant="square"
                  alt="Google Logo"
                  // we use the Google logo as the avatar
                  // the avatar is disabled when the Auth process is in progress
                  src={geeLogo}
                />
                {t("auth.signin")}
              </Button>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box className={classes.content} bgcolor="#343434">
        <Container maxWidth="md" display="flex" flexdirection="column">
          <Typography
            className={classes.title}
            variant="h3"
            align="center"
            style={{ color: "white" }} // Set text color to white
          >
            {t("home.instructions.title")}
          </Typography>
          <Grid container p={5} justifyContent="center" alignItems="center">
            <Grid item xs={12}>
              <Typography
                variant="body1"
                className={classes.spaced_text}
                align="center"
                style={{ color: "white" }} // Set text color to white
              >
                {t("home.instructions.text")}
              </Typography>
            </Grid>
          </Grid>
          <Box display="flex" alignItems="center" justifyContent="center">
            <Button
              variant="outlined"
              className={classes.spaced_btn}
              color="secondary"
              href={t("home.instructions.linkManual")}
              style={{
                color: "white", // Set button text color to white
                borderColor: "white", // Set button border color to white
              }}
            >
              {t("home.instructions.btnManual")}
            </Button>
          </Box>
        </Container>
      </Box>

      <Box className={classes.content}>
        <Container maxWidth="md" display="flex" flexdirection="column">
          <Typography className={classes.title} variant="h3" align="center">
            {t("home.about.title")}
          </Typography>
          <Grid
            container
            spacing={3}
            justifyContent="center"
            alignItems="center"
          >
            <Grid item xs={12} md={6}>
              <img
                src="/logo.svg"
                alt={t("home.about.title")}
                className={classes.intro_gif}
                style={{ width: "70%" }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body1">{t("self.abstract")}</Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box className={classes.content} bgcolor="white">
        <Container maxWidth="md">
          <Typography
            className={classes.title}
            variant="h4"
            align="center"
            gutterBottom
          >
            {t("home.members.title")}
          </Typography>

          {/* UG-FEES Section */}
          <Box mb={2} pb={2} borderBottom={1} borderColor="grey.200">
            <Typography
              variant="subtitle1"
              color="primary"
              fontWeight="medium"
              gutterBottom
            >
              University of Guyana – Faculty of Earth and Environmental Sciences
              (UG-FEES)
            </Typography>
            <Grid container spacing={1}>
              <Grid item xs={12} md={4}>
                <Typography variant="body1" fontWeight="bold">
                  Temitope D. Timothy OYEDOTUN
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Principal Investigator
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body1">Esan HAMER</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body1">Gordon A. NEDD</Typography>
              </Grid>
            </Grid>
          </Box>

          {/* SIG Section */}
          <Box mb={2} pb={2} borderBottom={1} borderColor="grey.200">
            <Typography
              variant="subtitle1"
              color="primary"
              fontWeight="medium"
              gutterBottom
            >
              Spatial Informatics Group
            </Typography>
            <Grid container spacing={1}>
              {[
                "Karis TENNESON",
                "Ate POORTINGA",
                "M. Warizmi WAFIQ",
                "Weraphong SUARUANG",
                "Andréa Puzzi NICOLAU",
              ].map((name) => (
                <Grid key={name} item xs={12} md={4}>
                  <Typography variant="body1">{name}</Typography>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* UCL Section */}
          <Box mb={2}>
            <Typography
              variant="subtitle1"
              color="primary"
              fontWeight="medium"
              gutterBottom
            >
              UCL – Department of Geography
            </Typography>
            <Grid container spacing={1}>
              <Grid item xs={12} md={4}>
                <Typography variant="body1">Helene BURNINGHAM</Typography>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Box>

      <Box className={classes.content}>
        <Container maxWidth="md" display="flex" flexdirection="column">
          <Grid container spacing={9} justifyContent="center">
            <Grid item xs={12} md={6} align="center">
              <Typography className={classes.title} variant="h4">
                {t("home.sponsor.proposed")}
              </Typography>
              <Grid container spacing={3} justifyContent="center">
                <Grid item xs={12} md={6} align="center">
                  <Link href="" target="_blank" rel="noopener">
                    <img
                      src={"/univ_guyana.jpg"}
                      alt="University of Guyana"
                      className={classes.sponsor_img}
                      style={{ height: "120px", marginTop: "-35px" }}
                    />
                  </Link>
                </Grid>

                <Grid item xs={12} md={4} align="center">
                  <Link href="" target="_blank" rel="noopener">
                    <img
                      src={"/fees.jpg"}
                      alt="LIA"
                      className={classes.sponsor_img}
                      style={{ height: "120px", marginTop: "-35px" }}
                    />
                  </Link>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} md={6} align="center">
              <Typography className={classes.title} variant="h4">
                {t("home.sponsor.reengindeerd")}
              </Typography>
              <Grid container spacing={3} justifyContent="center">
                <Grid item xs={12} md={6} align="center">
                  <Link href="" target="_blank" rel="noopener">
                    <img
                      src={"/sig.jpg"}
                      alt="Spatial Informatics Group"
                      className={classes.sponsor_img}
                      style={{ height: "120px", marginTop: "-35px" }}
                    />
                  </Link>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} md={6} align="center">
              <Typography className={classes.title} variant="h4">
                {t("home.sponsor.support_grant")}
              </Typography>

              <Grid container spacing={3} justifyContent="center">
                <Grid item xs={12} md={12} align="center">
                  <Link href="http://cnpq.br/" target="_blank" rel="noopener">
                    <img
                      src={"/moore.jpg"}
                      alt="MOORE"
                      className={classes.sponsor_img}
                      style={{ height: "80px", marginTop: "-10px" }}
                    />
                  </Link>
                  <Typography className={classes.spaced_text} variant="body1">
                    {t("home.sponsor.moore_desc")}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} md={6} align="center">
              <Typography className={classes.title} variant="h4">
                {t("home.sponsor.grant_admin")}
              </Typography>

              <Grid container spacing={3} justifyContent="center">
                <Grid item xs={12} md={12} align="center">
                  <Link href="http://cnpq.br/" target="_blank" rel="noopener">
                    <img
                      src={"/futureearth.jpg"}
                      alt="Future Earth"
                      className={classes.sponsor_img}
                      style={{ height: "80px", marginTop: "-10px" }}
                    />
                  </Link>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Snackbar
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        className={classes.snack}
        open={devAdvOpen}
        autoHideDuration={3000}
        style={{ display: "none" }}
        onClose={handleDevAdvClose}
        TransitionComponent={TransitionLeft}
      >
        <Alert
          elevation={5}
          variant="filled"
          onClose={handleDevAdvClose}
          severity="warning"
        >
          {t("home.warning.text")}{" "}
          <Link color="textPrimary" href="/problems">
            {t("home.warning.link")}
          </Link>
        </Alert>
      </Snackbar>

      <Snackbar
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        className={classes.snack}
        open={cookiesAdvOpen}
        onClose={handleCookiesAccept}
        message={t("home.cookies.text")}
        action={
          <React.Fragment>
            <Button
              color="secondary"
              size="small"
              onClick={handleCookiesAccept}
            >
              {t("home.cookies.link")}
            </Button>
          </React.Fragment>
        }
      ></Snackbar>
    </HomePageLayout>
  );
}

export default HomePage;
